import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { RouterModule, ActivatedRoute } from "@angular/router";
import { TablerIconsModule } from "angular-tabler-icons";
import { MaterialModule } from "src/app/material.module";
import { TranslateModule } from "@ngx-translate/core";
import { Subscription, debounceTime, distinctUntilChanged, filter } from "rxjs";
import { NavService } from "src/app/services/nav.service";
import { SearchService, SearchQuery } from "src/app/services/search.service";
import { NavItem } from "src/app/layouts/full/vertical/sidebar/nav-item/nav-item";
import {
  filterNavItemsForSearch,
  getSearchableNavItems,
} from "src/app/shared/helpers/nav-search.helper";
import {
  ViewContextHistoryStateService,
  eventAction,
} from "src/app/shared/services/view-context-history-state.service";

interface HeaderSearchViewState {
  searchQuery: string;
  resultCount: number;
  recentSearches: SearchQuery[];
}

@Component({
  selector: "app-header-search",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MaterialModule,
    TablerIconsModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: "./header-search.component.html",
  styleUrl: "./header-search.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderSearchComponent implements OnInit, OnDestroy {
  @ViewChild("searchInput") searchInput?: ElementRef<HTMLInputElement>;

  readonly searchControl = new FormControl("");

  protected panelOpen = false;
  protected recentSearches: SearchQuery[] = [];
  protected filteredResults: NavItem[] = [];

  private readonly navService = inject(NavService);
  private readonly searchService = inject(SearchService);
  private readonly route = inject(ActivatedRoute);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly viewContextHistorySvc =
    inject(ViewContextHistoryStateService<HeaderSearchViewState>);

  private subscriptions: Subscription[] = [];

  private get searchableNavItems(): NavItem[] {
    return getSearchableNavItems(this.navService.navItems());
  }

  ngOnInit(): void {
    this.recordSupportHistory(
      "view_ready",
      "Header search control initialized",
    );

    this.subscriptions.push(
      this.searchService.recentSearches$.subscribe((searches) => {
        this.recentSearches = searches;
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.push(
      this.searchControl.valueChanges.subscribe((query) => {
        const normalizedQuery = query || "";
        this.filteredResults = this.getFilteredItems(normalizedQuery);
        this.openPanel();
        this.cdr.markForCheck();
      }),
    );

    this.subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          debounceTime(600),
          distinctUntilChanged(),
          filter((query) => (query || "").trim() !== ""),
        )
        .subscribe((query) => {
          const normalizedQuery = query || "";
          const resultCount = this.getFilteredItems(normalizedQuery).length;
          this.searchService.addSearch(normalizedQuery, resultCount);
          this.recordSupportHistory(
            "search_performed",
            `Search features/pages: "${normalizedQuery}" - ${resultCount} matches found`,
          );
        }),
    );

    this.subscriptions.push(
      this.searchService.openSearch$.subscribe(() => {
        this.openPanel();
        this.focusSearchInput();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closePanel();
    }
  }

  protected onSearchShellPointerDown(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (target.closest(".header-search__panel")) {
      return;
    }

    if (target.closest("button, a, [role='button']")) {
      return;
    }

    if (target.closest("input, textarea")) {
      this.openPanel();
      return;
    }

    event.preventDefault();
    this.openPanel();
    this.focusSearchInput();
  }

  protected onInputFocus(): void {
    this.openPanel();
  }

  protected onSearchInput(): void {
    this.openPanel();
  }

  protected onInputKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.preventDefault();
      this.closePanel();
      this.searchInput?.nativeElement.blur();
      return;
    }

    this.openPanel();
  }

  protected openPanel(): void {
    this.panelOpen = true;
    this.cdr.markForCheck();
  }

  protected closePanel(): void {
    if (!this.panelOpen) {
      return;
    }

    this.panelOpen = false;
    this.cdr.markForCheck();
  }

  protected selectRecentSearch(query: string): void {
    this.searchControl.setValue(query, { emitEvent: false });
    this.filteredResults = this.getFilteredItems(query);
    this.searchService.addSearch(query, this.filteredResults.length);
    this.recordSupportHistory(
      "search_performed",
      `Selected recent search: "${query}"`,
    );
    this.openPanel();
    this.focusSearchInput();
    this.cdr.markForCheck();
  }

  protected removeRecentSearch(event: Event, query: string): void {
    event.stopPropagation();
    this.searchService.removeSearch(query);
    this.recordSupportHistory(
      "search_performed",
      `Removed search from history: "${query}"`,
    );
  }

  protected clearAllHistory(): void {
    this.searchService.clearHistory();
    this.recordSupportHistory("search_performed", "Cleared all search history");
  }

  protected onResultSelected(): void {
    this.closePanel();
    this.searchControl.setValue("", { emitEvent: false });
    this.filteredResults = [];
    this.cdr.markForCheck();
  }

  protected get hasQuery(): boolean {
    return (this.searchControl.value || "").trim() !== "";
  }

  private getFilteredItems(searchText: string): NavItem[] {
    return filterNavItemsForSearch(this.searchableNavItems, searchText);
  }

  private focusSearchInput(): void {
    queueMicrotask(() => {
      this.searchInput?.nativeElement.focus();
      this.cdr.markForCheck();
    });
  }

  private recordSupportHistory(
    eventAction: eventAction,
    eventDescription: string,
  ): void {
    this.viewContextHistorySvc.createContext({
      titleView: "Header Search",
      eventAction,
      eventDescription,
      thisState: {
        searchQuery: this.searchControl.value || "",
        resultCount: this.filteredResults.length,
        recentSearches: this.recentSearches,
      },
      route: this.route.snapshot,
      setAutomaticallyScreenshot: false,
    });
  }
}
